import { RenewalAlert, RenewalStatus, User } from '@policyvision/contracts';
import { ParseKeys } from 'i18next';
import { SearchIcon, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MultiSelectFilter } from '@/common/components/table/MultiSelectFilter';
import {
  SelectFilter,
  SelectFilterOption,
} from '@/common/components/table/SelectFilter';
import { Badge } from '@/common/components/ui/badge';
import { Button } from '@/common/components/ui/button';
import { Input } from '@/common/components/ui/input';
import RangeSlider from '@/common/components/ui/range-slider';
import {
  Select,
  SelectContent,
  SelectTrigger,
} from '@/common/components/ui/select';
import { useDebouncer } from '@/common/hooks/useDebouncer';
import { useUsers } from '@/user/hooks/useUser';

import { Filters } from '../types/FilterTypes';
import { COLORS } from '../utils/badges';

const EXPIRICY: SelectFilterOption[] = [
  { value: 'all', label: 'renewal.all' },
  { value: '60', label: 'renewal.filters.expiration.60less' },
  { value: '120', label: 'renewal.filters.expiration.120less' },
  { value: '180', label: 'renewal.filters.expiration.180less' },
  { value: '200', label: 'renewal.filters.expiration.200less' },
];

const ALERTS: RenewalAlert[] = ['MARC', 'ANL', 'RMEV'];
const STATUS: RenewalStatus[] = [
  'INITIATED',
  'IN_PROGRESS',
  'STANDBY',
  'STANDBY_INSURER',
  'DONE',
];

const RENEWAL_AMOUNT_MIN = 0;
const RENEWAL_AMOUNT_MAX = 100000;

const formatAmount = (amount: number) =>
  new Intl.NumberFormat('fr-CA', {
    maximumFractionDigits: 0,
  }).format(amount);

interface RenewalFiltersProps {
  filters: Filters;
  onFilterChange: (value: string | string[] | number[], key: string) => void;
  onClearFilters: () => void;
}

const RenewalFilters = ({
  filters,
  onFilterChange,
  onClearFilters,
}: RenewalFiltersProps) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState(filters.policyNumber ?? '');
  const [searchPlaceHolder, setSearchPlaceHolder] = useState(
    t('renewal.searchRenewal'),
  );
  const [premiumWidth, setPremiumWidth] = useState('w-50');
  const { users } = useUsers();
  const debouncedOnFilterChange = useDebouncer(onFilterChange);

  const handleClearFilters = () => {
    setSearchValue('');
    onClearFilters();
    setPremiumWidth('w-50');
    setSearchPlaceHolder(t('renewal.searchRenewal'));
  };

  return (
    <div className="absolute top-15 left-0 z-2 w-full">
      <div className="bg-terre-50 w-full py-4 px-6 border-b border-border">
        <Input
          className="bg-white!"
          placeholder={searchPlaceHolder}
          value={searchValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            debouncedOnFilterChange(e.target.value, 'policyNumber');
            if (e.target.value === '') {
              setSearchPlaceHolder(t('renewal.searchRenewal'));
            }
          }}
          endAdornment={
            searchValue ? (
              <X
                size={16}
                className="stroke-sidebar-border stroke-2 cursor-pointer hover:stroke-primary"
                onClick={() => {
                  setSearchPlaceHolder(t('renewal.searchRenewal'));
                  setSearchValue('');
                  onFilterChange('', 'policyNumber');
                }}
              />
            ) : (
              <SearchIcon
                size={16}
                className="stroke-sidebar-border stroke-2 pointer-events-none"
              />
            )
          }
          onFocus={() => setSearchPlaceHolder('')}
          onBlur={() => {
            if (!searchValue) {
              setSearchPlaceHolder(t('renewal.searchRenewal'));
            }
          }}
        />
      </div>
      <div className="bg-terre-50 w-full py-2 px-6 border-b border-border flex items-center gap-2">
        <SelectFilter
          label={t('renewal.expiresIn')}
          options={EXPIRICY.map((e) => ({
            ...e,
            label: t(e.label as ParseKeys),
          }))}
          value={filters.expirationDaysMax?.toString() ?? 'all'}
          onChange={(value) => onFilterChange(value, 'expirationDaysMax')}
          textSize="text-xs"
        />

        <Select
          onOpenChange={(isOpen) => {
            if (isOpen) {
              setPremiumWidth('w-80');
            } else if (!filters.renewalAmountMin && !filters.renewalAmountMax) {
              setPremiumWidth('w-50');
            }
          }}
        >
          <SelectTrigger
            className={`h-7 text-xs cursor-pointer ${premiumWidth} transition-all duration-300`}
          >
            <span className="text-muted-foreground">
              {t('renewal.renewalPremiumLabel')}:
            </span>
            <span className="tabular-nums">
              {filters.renewalAmountMin !== undefined &&
              filters.renewalAmountMax !== undefined
                ? `${formatAmount(filters.renewalAmountMin)} $ - ${formatAmount(filters.renewalAmountMax)} $`
                : null}
            </span>
          </SelectTrigger>
          <SelectContent className="w-(--radix-select-trigger-width)">
            <RangeSlider
              value={[
                filters.renewalAmountMin || 0,
                filters.renewalAmountMax || 0,
              ]}
              min={RENEWAL_AMOUNT_MIN}
              max={RENEWAL_AMOUNT_MAX}
              onValueChange={(value: number[]) => {
                onFilterChange(String(value[0]), 'renewalAmountMin');
                onFilterChange(String(value[1]), 'renewalAmountMax');
              }}
            />
          </SelectContent>
        </Select>
        <MultiSelectFilter
          minWidth={'unset'}
          label={t('renewal.assignedToLabel')}
          options={users.map((user: User) => ({
            value: user.id,
            label: user.displayName,
          }))}
          values={filters.assignedUserIds ?? []}
          placeholder={t('renewal.all')}
          onChange={(values) => onFilterChange(values, 'assignedUserIds')}
          textSize="text-xs"
          className="font-normal px-3 h-full"
        />
        <SelectFilter
          minWidth={'unset'}
          label={t('renewal.alertsLabel')}
          options={ALERTS.map((alert) => ({
            value: alert,
            label: <Badge variant={COLORS.ALERTS[alert]}>{alert}</Badge>,
          }))}
          value={filters.alerts ?? ''}
          onChange={(value) => onFilterChange(value, 'alerts')}
          textSize="text-xs"
        />

        <SelectFilter
          minWidth={'unset'}
          label={t('renewal.statusLabel')}
          options={STATUS.map((status) => ({
            value: status,
            label: (
              <Badge variant={COLORS.STATUS[status]}>
                {t(`renewal.${status}`)}
              </Badge>
            ),
          }))}
          value={filters.status ?? ''}
          onChange={(value) => onFilterChange(value, 'status')}
          textSize="text-xs"
        />
        {/* <Select value="all">
          <SelectTrigger>
            <SelectValue>
              <span className="text-muted-foreground">
                {t('renewal.columnsLabel')} :
              </span>{' '}
              {t('renewal.all')}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('renewal.all')}</SelectItem>
          </SelectContent>
        </Select> */}
        <Button variant="ghost" onClick={handleClearFilters}>
          {t('renewal.removeFilters')}
        </Button>
      </div>
    </div>
  );
};

export default RenewalFilters;
